import React, { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { toast } from "react-toastify";
import { getRentalSurvey, sendRentalSurvey, surveyStatusLabel } from "../../../data/loca/satisfaction";
import { Stars } from "../satisfaction/SatisfactionStats";
import "../../../assets/css/satisfaction.css";

// Vue loueur, page d'une location terminee : etat du questionnaire de satisfaction et reponses du locataire.

export default function RentalSatisfactionPanel({ rentalPublicId }) {
    const [survey, setSurvey] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        getRentalSurvey(rentalPublicId)
            .then((data) => {
                if (mounted) {
                    setSurvey(data);
                }
            })
            .catch(() => {
                if (mounted) {
                    setSurvey(null);
                }
            })
            .finally(() => {
                if (mounted) {
                    setLoading(false);
                }
            });
        return () => {
            mounted = false;
        };
    }, [rentalPublicId]);

    const send = async () => {
        setSending(true);
        try {
            const updated = await sendRentalSurvey(rentalPublicId);
            setSurvey(updated);
            toast.success("Invitation envoyée au locataire.");
        } catch (error) {
            toast.error(error?.message || "Envoi impossible.");
        } finally {
            setSending(false);
        }
    };

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(survey.link);
            toast.info("Lien copié.");
        } catch {
            toast.warn("Impossible de copier le lien.");
        }
    };

    if (loading) {
        return (
            <div className="sat-rental-panel">
                <h3>Satisfaction du locataire</h3>
                <p className="sat-empty">Chargement…</p>
            </div>
        );
    }

    if (!survey) {
        return null;
    }

    const status = survey.exists === false ? "NotSent" : survey.status;
    const statusClass = status === "Completed" ? "is-completed" : status === "Started" ? "is-started" : "";
    const answered = (survey.answers ?? []).filter((answer) => answer.valueNumber != null || (answer.valueText ?? "").trim() !== "");

    return (
        <div className="sat-rental-panel">
            <h3>
                Satisfaction du locataire
                <span className={`sat-status ${statusClass}`}>{surveyStatusLabel(status)}</span>
            </h3>

            {survey.exists === false && (
                <p className="sat-empty">
                    {survey.enabled
                        ? "Aucun questionnaire n'a encore été envoyé pour cette location."
                        : "L'envoi automatique du questionnaire est désactivé dans vos paramètres."}
                </p>
            )}

            {survey.exists !== false && (
                <>
                    {survey.score != null && (
                        <p>
                            <Stars score={survey.score} /> <strong>{survey.score}/5</strong>
                            {survey.wouldRecommend != null && (
                                <span className="text-muted"> · {survey.wouldRecommend ? "recommanderait" : "ne recommanderait pas"} le loueur</span>
                            )}
                        </p>
                    )}
                    {survey.invitationSentAt && (
                        <p className="text-muted">
                            Invitation envoyée le {new Date(survey.invitationSentAt).toLocaleDateString("fr-FR")}
                            {survey.reminderSentAt ? ` · relance automatique le ${new Date(survey.reminderSentAt).toLocaleDateString("fr-FR")}` : ""}
                            {survey.completedAt ? ` · terminé le ${new Date(survey.completedAt).toLocaleDateString("fr-FR")}` : ""}
                            {" · "}{survey.answeredCount}/{survey.totalCount} réponse{survey.totalCount > 1 ? "s" : ""}
                        </p>
                    )}
                    {answered.length > 0 && (
                        <div className="sat-answers">
                            {answered.map((answer) => (
                                <div key={answer.questionPublicId} className={`sat-answer${answer.type === "Text" ? " is-text" : ""}`}>
                                    <span>{answer.section === "tool" && answer.toolName ? `${answer.toolName} · ` : ""}{answer.label}</span>
                                    <strong>{answer.type === "Rating" ? <><Stars score={answer.valueNumber} /> {answer.displayValue}</> : answer.displayValue}</strong>
                                </div>
                            ))}
                        </div>
                    )}
                    {answered.length === 0 && status !== "NotSent" && (
                        <p className="sat-empty">Le locataire n'a pas encore répondu.</p>
                    )}
                </>
            )}

            <div className="flex flex-wrap gap-2 mt-2">
                <Button
                    label={status === "NotSent" ? "Envoyer le questionnaire" : "Renvoyer l'invitation"}
                    icon="pi pi-send"
                    size="small"
                    severity="secondary"
                    outlined
                    loading={sending}
                    onClick={send}
                />
                {survey.link && (
                    <Button label="Copier le lien" icon="pi pi-copy" size="small" text onClick={copyLink} />
                )}
            </div>
        </div>
    );
}
